// src/ui/MessageTicks.jsx — 1:1 port of the app's src/components/MessageTicks.tsx.
// Same glyph paths, same status rules, same colors, so web and phone are identical:
//   pending   → clock
//   sent      → ✓   (single, grey)
//   delivered → ✓✓  (double, grey)
//   read      → ✓✓  (double, the one read blue)
//   failed    → red !
import React from 'react';
import { isDeadSend } from '../services/sendState';
import { isSendConfirmed } from '../services/sendOutcome';
import { OFFICIAL_UID } from '../services/support';
import { msOf } from '../services/joinFloor';

/* 🔵 ONE read colour, as the app (src/utils/tickColors READ_BLUE): the list was green and the bubbles
   another blue; every read tick is this blue now, on the phone, here and on the home-screen widgets. */
export const TICK_READ = '#34B7F1';
export const TICK_READ_BLUE = TICK_READ;
const TICK_FAILED_RED = '#FF3B30';

/** Derive status from receipt flags — identical logic to the app. */
export function getMessageStatus(message, opts) {
  if (!message) return 'pending';
  /* 🟢 the shared rule — services/sendState.ts. This line used to know `failed`
     and not the two offline states, the same gap that showed the phone a 0%
     ring on a photo sent with no internet. */
  /* 🟥 …UNLESS THE SERVER HAS IT. A row carrying a `seq` AND a stale
     `uploadStatus: 'failed'` drew the red ! — and that pair is the reported
     bug's resting state, not a curiosity: the send landed, something declared
     it failed, the ack arrived a moment later and nothing cleared the flag.
     Evidence beats a leftover flag; services/sendOutcome.ts owns the rule. */
  if (isDeadSend(message) && !isSendConfirmed(message)) return 'failed';
  /* ✓ ONLY THE SERVER'S WORD. A row with an id used to count as sent — but our own write is echoed
     back from the local queue WITH its id before the server has it (no internet, or a wifi with
     none). A seq (the server's ack) or a committed row is the check; the echo waits on the clock. */
  const isSent = message.seq != null || (!message._pendingWrite && !!(message.sent || message.id));
  if (opts?.isBotOrSelf) return isSent ? 'read' : 'pending';
  if (message.seen) return 'read';
  if (message.delivered) return 'delivered';
  if (isSent) return 'sent';
  return 'pending';
}

function msgMillis(m) {
  // 🟥 `?? m.ts`: the SIDEBAR previews carry their time as `ts` (previews.js),
  //    not `timestamp`. Reading only `timestamp` made every list row fall through
  //    to Date.now() — "this message was sent just now" — so no read/delivered
  //    watermark could ever be >= it and the list ticks were stuck grey forever,
  //    while the open chat (real message objects, real `timestamp`) worked fine.
  const t = m?.timestamp ?? m?.ts;
  if (!t) return Date.now();
  if (typeof t === 'number') return t;
  if (t.seconds) return t.seconds * 1000;
  if (typeof t.toDate === 'function') { try { return t.toDate().getTime(); } catch { return Date.now(); } }
  return new Date(t).getTime() || Date.now();
}

/**
 * 🟢 THE REAL RULE. The server does NOT flip per-message flags — it advances
 * forward-only WATERMARKS on the chat doc:
 *     chats/{id}.deliveredUpTo[uid] / readUpTo[uid] = millis of the newest
 *     message that uid has RECEIVED / READ.
 * So a sent message's ticks are derived by comparing its own millis against the
 * PEERS' watermarks. (Without this the ticks were stuck on a single check forever.)
 * Groups follow the app's rule: green only when EVERY other member has read it.
 */
export function deriveReceiptStatus(message, chat, myUid) {
  if (!message) return 'pending';
  /* 🟥 …UNLESS THE SERVER HAS IT. A row carrying a `seq` AND a stale
     `uploadStatus: 'failed'` drew the red ! — and that pair is the reported
     bug's resting state, not a curiosity: the send landed, something declared
     it failed, the ack arrived a moment later and nothing cleared the flag.
     Evidence beats a leftover flag; services/sendOutcome.ts owns the rule. */
  if (isDeadSend(message) && !isSendConfirmed(message)) return 'failed';
  /* ✓ ONLY THE SERVER'S WORD. A row with an id used to count as sent — but our own write is echoed
     back from the local queue WITH its id before the server has it (no internet, or a wifi with
     none). A seq (the server's ack) or a committed row is the check; the echo waits on the clock. */
  const isSent = message.seq != null || (!message._pendingWrite && !!(message.sent || message.id));
  if (!isSent) return 'pending';

  /* 🟥 a member who LEFT never reads again, so once anyone had left every group message waited on them
        and stayed at one tick. 🟢 The members still in the group (the app's seqReceipts rule). */
  const _left = Array.isArray(chat?.leftParticipants) ? chat.leftParticipants : [];
  /* 👥 …and the members when it was SENT: someone added later never received it (the app's
        readersAtSend) — without this, adding a member turned every old blue message grey again */
  const _joined = chat?.isGroup ? (chat?.joinedAt || null) : null;
  const _sentMs = _joined ? msgMillis(message) : 0;
  const peers = (chat?.participants || []).filter((u) => u && u !== myUid && !_left.includes(u)
    && !(_joined && msOf(_joined[u]) > _sentMs));

  /* 💬 THE OFFICIAL ALLO ACCOUNT SETTLES ON TWO GREY TICKS, ALWAYS.
     It is a broadcast address, not a person: nothing on the other side writes a
     read receipt, and the chat's own read-receipts switch is gone from the info
     pane because there is nobody to send one to. Left to the rules below, its
     messages would come to rest wherever the fallback flags happened to land —
     one grey tick when nothing set `delivered`, and a blue pair for ever if any
     writer set `seen` — which is the difference between "Allo has this" and
     "somebody at Allo read this", and only one of those is true.
     Reaching the server IS delivery here, so a sent message is delivered and
     never read, whatever its type and whoever wrote its flags. */
  if (peers.length && peers.every((p) => p === OFFICIAL_UID)) return 'delivered';

  // 🟢 NOTE TO SELF: you ARE the recipient, so the moment the message exists it has
  //    been delivered and read — the app shows ✓✓ green for every note. There are no
  //    peers here, so the watermark rule below can never fire and the ticks would
  //    otherwise sit on a single grey check for ever.
  if (!peers.length && isSent) return 'read';
  /* ✓✓ / blue are about THIS message: a numbered message is delivered / read when each peer's seq
     cursor (chats/{id}.receiptsSeq, advanced per message by the server) has reached ITS seq — the
     phone's rule (src/utils/seqReceipts.ts). The time watermarks below cannot speak for it: in a
     burst, the first message's receipt time covered every message typed after it, still unsent. */
  if (peers.length && message.seq != null) {
    const s = Number(message.seq);
    const rs = chat?.receiptsSeq?.read || {};
    const ds = chat?.receiptsSeq?.delivered || {};
    if (peers.every((p) => (Number(rs[p]) || 0) >= s)) return 'read';
    if (peers.every((p) => (Number(rs[p]) || 0) >= s || (Number(ds[p]) || 0) >= s)) return 'delivered';
    return 'sent';
  }
  /* a recent message without its seq is waiting for it — not pre-seq history the watermarks describe */
  if (peers.length && Date.now() - msgMillis(message) < 10 * 60 * 1000) return 'sent';
  if (peers.length) {
    const ms = msgMillis(message);
    const read = chat?.readUpTo || {};
    const del = chat?.deliveredUpTo || {};
    if (peers.every((p) => (read[p] || 0) >= ms)) return 'read';
    if (peers.every((p) => (del[p] || 0) >= ms)) return 'delivered';
  }
  // fall back to per-message flags (covers receipts written directly by a client)
  if (message.seen) return 'read';
  if (message.delivered) return 'delivered';
  return 'sent';
}

function Ticks({ double, color, size }) {
  const h = Math.round(size * 0.72);
  const w = double ? Math.round(h * (14 / 11)) : Math.round(h * (13 / 11));
  const sw = 1.6;
  if (double) {
    return (
      <svg width={w} height={h} viewBox="0 0 14 11" style={{ display: 'block' }}>
        <path d="M1 5.5 L4 9 L9 2" stroke={color} strokeWidth={sw} fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4.6 5.5 L7.6 9 L12.6 2" stroke={color} strokeWidth={sw} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width={w} height={h} viewBox="0 0 13 11" style={{ display: 'block' }}>
      <path d="M1.5 5.5 L5 9 L11.5 2" stroke={color} strokeWidth={sw} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function MessageTicks({ status, size = 16, color = 'currentColor', readColor = TICK_READ, style }) {
  let content = null;
  if (status === 'failed') {
    content = <span style={{ color: TICK_FAILED_RED, fontWeight: 700, fontSize: size * 0.8, lineHeight: 1 }}>!</span>;
  } else if (status === 'pending') {
    content = (
      <svg width={size * 0.72} height={size * 0.72} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" style={{ display: 'block' }}>
        <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
      </svg>
    );
  } else if (status === 'read') {
    content = <Ticks double color={readColor} size={size} />;
  } else if (status === 'delivered') {
    content = <Ticks double color={color} size={size} />;
  } else {
    content = <Ticks double={false} color={color} size={size} />;
  }
  return <span style={{ display: 'inline-flex', alignItems: 'center', ...style }}>{content}</span>;
}
